import { ApolloClient } from "@apollo/client"
import {
  logout as caLogout,
  customerAccountFetch,
  getAccessToken,
  handleCallback,
  startLogin,
} from "@auth/customerAccount"
import createContentfulClient from "@cms/createApolloClient"
import { GetCustomerPurchasedProductsData } from "@cms/graphql/queries"
import { ContentfulPurchasedProduct } from "@cms/types"
import {
  CUSTOMER_COUNTRY_CODE_KEY,
  CUSTOMER_STATE_CODE_KEY,
  CUSTOMER_ZIP_CODE_KEY,
} from "@constants/storageKeys"
import { createModel } from "@rematch/core"
import createShopifyCartClient from "@shopify/createApolloCartClient"
import createShopifyClient from "@shopify/createApolloClient"
import {
  CustomerAddressCreate,
  CustomerAddressDelete,
  CustomerAddressUpdate,
  CustomerDefaultAddressUpdate,
  UpdateCartBuyerIdentity,
} from "@shopify/graphql/mutations"
import { GetCustomerAccount } from "@shopify/graphql/queries"
import { CustomerAccountData } from "@shopify/types"
import { CountryCode, resolveCartCountryCode } from "@utils/cartMarket"
import { getIn } from "@utils/index"
import { getLocalStorage, writeLocalStorage } from "@utils/storage"

import { PurchasedProducts } from "../types"
import {
  buildPurchasedProducts,
  fetchCustomerLocation,
  graphQLRequest,
  UNKNOWN_LOCATION,
} from "../utils"
import { RootModel } from "./index"

export interface CustomerState {
  customerDataFetched: boolean
  customerLocation: {
    customerCountryCode: string
    customerStateCode: string
    customerZipCode: string
  }
  customerLocationFetched: boolean
  customerLoggedIn: boolean
  data: CustomerAccountData
  purchasedProducts: PurchasedProducts
}

const customer = createModel<RootModel>()({
  effects: (dispatch: any) => ({
    async addressCreate({ setDefault, ...address }, rootState: any) {
      const customerAccessToken = getAccessToken()
      if (customerAccessToken) {
        const client: ApolloClient<any> = createShopifyClient()
        const result = await graphQLRequest(client.mutate, {
          mutation: CustomerAddressCreate,
          variables: {
            address,
            customerAccessToken,
          },
        })

        dispatch.customer.fetchCustomerData()

        const defaultAddress = rootState.customer.data.defaultAddress

        if (!defaultAddress || setDefault) {
          const createdId = getIn(
            result,
            "data.customerAddressCreate.customerAddress.id",
            ""
          )
          dispatch.customer.updateDefaultAddress(createdId)
        }

        return result
      }
      return undefined
    },

    async addressDelete(addressId) {
      const customerAccessToken = getAccessToken()
      if (customerAccessToken) {
        const client: ApolloClient<any> = createShopifyClient()
        const result = await graphQLRequest(client.mutate, {
          mutation: CustomerAddressDelete,
          variables: {
            customerAccessToken,
            id: addressId,
          },
        })

        dispatch.customer.fetchCustomerData()

        return result
      }
      return undefined
    },

    async addressUpdate({ id, setDefault, ...address }, rootState: any) {
      const customerAccessToken = getAccessToken()
      if (customerAccessToken) {
        const client: ApolloClient<any> = createShopifyClient()
        const result = await graphQLRequest(client.mutate, {
          mutation: CustomerAddressUpdate,
          variables: {
            address,
            customerAccessToken,
            id,
          },
        })
        dispatch.customer.fetchCustomerData()

        const defaultAddress = rootState.customer.data.defaultAddress

        if (!defaultAddress || (setDefault && defaultAddress.id !== id)) {
          dispatch.customer.updateDefaultAddress(id)
        }

        return result
      }
      return undefined
    },

    async authBeginLogin() {
      await startLogin()
    },

    async authCompleteLogin({ callback }) {
      try {
        await handleCallback(new URLSearchParams(window.location.search))
        await dispatch.customer.fetchCustomerData({ callback })
      } catch (e) {
        dispatch.customer.loginError(String(e))
      }
    },

    async authLogout() {
      dispatch.customer.removeCustomerData()
      dispatch.customer.setCustomerLoggedIn(false)
      await caLogout(window.location.origin)
    },

    async fetchCustomerData({
      callback,
    }: { callback?: (data: any) => void } = {}) {
      const bearer = getAccessToken()
      let customerAccountData

      if (bearer) {
        try {
          const res = await customerAccountFetch<{ data?: { customer?: any } }>(
            GetCustomerAccount
          )

          customerAccountData = res?.data?.customer

          if (customerAccountData && callback) {
            const email = customerAccountData?.emailAddress?.emailAddress
            const id = customerAccountData?.id
            callback({ email, id: id.replace("gid://shopify/Customer/", "") })
          }
        } catch (error) {
          console.error("Error fetching customer data:", error)
        }
      }

      if (!bearer || !customerAccountData)
        dispatch.customer.removeCustomerData()
      dispatch.customer.setCustomerData(
        customerAccountData ?? ({} as CustomerAccountData)
      )
      dispatch.customer.setCustomerLoggedIn(Boolean(customerAccountData))
      dispatch.customer.setCustomerDataFetched(true)
    },

    async fetchCustomerLocation() {
      const customerLocation = await fetchCustomerLocation()
      dispatch.customer.updateCustomerLocation(customerLocation)
      return customerLocation
    },

    async fetchPurchasedProducts({ productIds }) {
      const client: ApolloClient<any> = createContentfulClient()
      let productData = []

      if (productIds) {
        const { data, errors } = await graphQLRequest(client.query, {
          query: GetCustomerPurchasedProductsData,
          variables: { ids: productIds },
        })

        if (errors) {
          console.error(errors)
        }

        productData = getIn(data, "productVariantCollection.items", [])
      }

      dispatch.customer.setPurchasedProducts(productData)
    },

    async updateCartBuyerIdentity({ cartId }: any, rootState: any) {
      const customerAccessToken = getAccessToken()

      if (customerAccessToken) {
        const client: ApolloClient<any> = createShopifyCartClient()
        const geoCountryCode = rootState.customer?.customerLocation
          ?.customerCountryCode as CountryCode | undefined
        const { country: cartCountry } = resolveCartCountryCode({
          geoCountryCode,
        })

        await graphQLRequest(client.mutate, {
          mutation: UpdateCartBuyerIdentity,
          variables: {
            buyerIdentity: {
              countryCode: cartCountry,
              customerAccessToken,
            },
            cartCountry,
            cartId,
          },
        })
      }
    },

    updateCustomerLocation(_customerLocation) {
      const _window = typeof window !== "undefined" && (window as any)
      const customerLocation =
        _window.location?.hostname === "localhost"
          ? {
              customerCountryCode: "US",
              customerStateCode: "CA",
              customerZipCode: "92780",
            }
          : _customerLocation

      writeLocalStorage(CUSTOMER_ZIP_CODE_KEY, customerLocation.customerZipCode)
      writeLocalStorage(
        CUSTOMER_STATE_CODE_KEY,
        customerLocation.customerStateCode
      )
      writeLocalStorage(
        CUSTOMER_COUNTRY_CODE_KEY,
        customerLocation.customerCountryCode
      )

      if (customerLocation.customerCountryCode) {
        _window.maestraAreaId = customerLocation.customerCountryCode
      }

      dispatch.customer.setCustomerLocation(customerLocation)
      dispatch.customer.setCustomerLocationFetched(true)
    },

    updateCustomerZipCode(customerZipCode, rootState) {
      writeLocalStorage(CUSTOMER_ZIP_CODE_KEY, customerZipCode)
      dispatch.customer.setCustomerLocation({
        customerCountryCode:
          rootState.customer.customerLocation.customerCountryCode ||
          UNKNOWN_LOCATION,
        customerZipCode,
      })
    },

    async updateDefaultAddress(addressId) {
      const customerAccessToken = getAccessToken()
      if (customerAccessToken) {
        const client: ApolloClient<any> = createShopifyClient()
        const result = await graphQLRequest(client.mutate, {
          mutation: CustomerDefaultAddressUpdate,
          variables: {
            addressId,
            customerAccessToken,
          },
        })

        dispatch.customer.fetchCustomerData()

        return result
      }
      return undefined
    },
  }),
  name: "customer",
  reducers: {
    loginError(state: CustomerState, message: string) {
      return {
        ...state,
        customerDataFetched: false,
        customerLoggedIn: false,
        error: message,
      }
    },

    removeCustomerData(state: CustomerState) {
      return {
        ...state,
        data: {} as CustomerAccountData,
      }
    },

    setCustomerData(state: CustomerState, data: CustomerAccountData) {
      return {
        ...state,
        data,
      }
    },

    setCustomerDataFetched(state: CustomerState, customerDataFetched: boolean) {
      return {
        ...state,
        customerDataFetched,
      }
    },

    setCustomerLocation(
      state: CustomerState,
      customerLocation: {
        customerCountryCode: string
        customerStateCode: string
        customerZipCode: string
      }
    ) {
      return {
        ...state,
        customerLocation,
      }
    },

    setCustomerLocationFetched(
      state: CustomerState,
      customerLocationFetched: boolean
    ) {
      return {
        ...state,
        customerLocationFetched,
      }
    },

    setCustomerLoggedIn(state: CustomerState, customerLoggedIn: boolean) {
      return {
        ...state,
        customerLoggedIn,
      }
    },

    setPurchasedProducts(
      state: CustomerState,
      data: Array<ContentfulPurchasedProduct>
    ) {
      return {
        ...state,
        purchasedProducts: {
          ...state.purchasedProducts,
          ...buildPurchasedProducts(data),
        },
      }
    },
  },
  selectors: (slice) => ({
    acceptsMarketing() {
      return slice((customer) =>
        ["PENDING", "SUBSCRIBED"].includes(
          customer.data?.emailAddress?.marketingState ?? ""
        )
      )
    },
  }),
  state: {
    customerDataFetched: false, // Fetch Customer data once
    customerLocation: {
      customerCountryCode:
        getLocalStorage(CUSTOMER_COUNTRY_CODE_KEY) || UNKNOWN_LOCATION,
      customerStateCode:
        getLocalStorage(CUSTOMER_STATE_CODE_KEY) || UNKNOWN_LOCATION,
      customerZipCode:
        getLocalStorage(CUSTOMER_ZIP_CODE_KEY) || UNKNOWN_LOCATION,
    },
    customerLocationFetched: false,
    customerLoggedIn: false,
    data: {} as CustomerAccountData,
    purchasedProducts: {},
  } as CustomerState,
})

export default customer
